import googleDriveIcon from '@/assets/Google-drive-icon.png'
import slackIcon from '@/assets/slack-icon.png'
import { Link } from '@/components/ui/Link'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { CAI_BRAND_SYSTEM_HREF, type ExternalRef, RefLink } from '../primitives'

const MARKETING_COLLATERAL: ExternalRef = {
  name: 'Marketing & Sales Collateral',
  href: 'https://drive.google.com/drive/folders/1fj3DBvY4umla480l6kBDNfzcd0JoJV2c',
}

/** Bulleted cell content; an array of nodes renders as a list. */
function Bullets({ items }: { items: React.ReactNode[] }) {
  return (
    <ul className="list-disc space-y-1 pl-4">
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  )
}

/** Ordered cell content — these run in sequence. Wider padding fits a two-digit marker. */
function Steps({ items }: { items: React.ReactNode[] }) {
  return (
    <ol className="list-decimal space-y-1 pl-5">
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ol>
  )
}

/** A skill name, in the page's mono systems voice. */
function Cmd({ children }: { children: string }) {
  return <span className="text-ink font-mono text-xs">{children}</span>
}

/** The Cai Brand System, as it lives in Claude Design. */
function BrandSystemLink() {
  return (
    <Link
      href={CAI_BRAND_SYSTEM_HREF}
      target="_blank"
      rel="noopener noreferrer"
      className="text-ink hover:text-brand-800 font-normal underline underline-offset-2"
    >
      Cai Brand System
    </Link>
  )
}

/** Both Claude Design flows open on the same note. */
const USES_BRAND_SYSTEM = (
  <em className="text-ink-500">
    Automatically uses <BrandSystemLink />
  </em>
)

/** Opening step of every flow; the tool name carries the weight. */
const START_IN_CLAUDE_DESIGN = (
  <>
    Start in <strong className="text-ink font-semibold">Claude Design</strong>
  </>
)

/** The marketing-webpage flow can be entered from either tool. */
const START_IN_CLAUDE_DESIGN_OR_CODE = (
  <>
    Start in <strong className="text-ink font-semibold">Claude Design</strong> or{' '}
    <strong className="text-ink font-semibold">Claude Code</strong>
  </>
)

const START_WITH_CLAUDE_CODE = (
  <>
    Start with <strong className="text-ink font-semibold">Claude Code</strong>
  </>
)

/** Value tags, set apart from the note prose. */
function Tags({ children }: { children: string }) {
  return <span className="text-ink-400 font-mono text-xs">{children}</span>
}

/** How both Claude Code flows get eyes on the work. */
const FEEDBACK_CHANNEL = (
  <span className="inline-flex items-start gap-1.5">
    {/* The Slack mark is not square, so height is fixed and width follows. */}
    <img src={slackIcon} alt="" className="mt-0.5 h-3.5 w-auto shrink-0" />
    <span>
      Create a Slack feedback channel, using the pattern <Cmd>#test-</Cmd>
    </span>
  </span>
)

interface Workflow {
  making: string
  /** Unbulleted lead-in naming the tool to open. */
  start: React.ReactNode
  /** Optional qualifier above the steps, when they only apply to one entry path. */
  stepsLead?: React.ReactNode
  how: React.ReactNode[]
  document: React.ReactNode
  notes: React.ReactNode | React.ReactNode[]
}

const WORKFLOWS: Workflow[] = [
  {
    making: 'One-pagers, ads, etc.',
    start: START_IN_CLAUDE_DESIGN,
    how: [
      USES_BRAND_SYSTEM,
      <>
        Run <Cmd>/cai-language-review</Cmd>
      </>,
      'Request feedback and comments via Share button in upper right',
      'Share button, export PDF',
    ],
    document: <RefLink icon={googleDriveIcon} target={MARKETING_COLLATERAL} />,
    notes: (
      <div className="flex flex-col gap-2">
        <p>Claude can be very verbose and create busy one-pagers. Strongly encourage that it be concise.</p>
        <p>
          We have three types of one-pagers documented in <BrandSystemLink />: For Signature, Product Offers,
          and Insurance 101 Explainer. If your content falls into one of those three buckets, use that name in
          your initial prompt to Claude Design.
        </p>
      </div>
    ),
  },
  {
    making: 'Marketing webpage',
    start: START_IN_CLAUDE_DESIGN_OR_CODE,
    stepsLead: 'If starting in Claude Design',
    how: [
      USES_BRAND_SYSTEM,
      <>
        Run <Cmd>/cai-language-review</Cmd>
      </>,
      'Request feedback and comments via Share button in upper right',
      'Share (button, upper right), Send to Claude Code',
      <>
        Run <Cmd>/cai-design-review</Cmd>, updating to our real components
      </>,
      <>
        Use <Cmd>/ship</Cmd> to publish to the Insure repo in GitHub
      </>,
      'Request review from an engineer',
    ],
    document: '',
    notes: [
      <>
        Specifically ensure that <Cmd>/cai-design-review</Cmd> swaps in your top nav and footer to our true
        components
      </>,
      'Remember to add PostHog events to your pages',
    ],
  },
  {
    making: 'App feature',
    start: START_WITH_CLAUDE_CODE,
    how: [
      <>
        Use <Cmd>/work</Cmd> to set up branch, worktree, local environment, etc.
      </>,
      'Open Design System (this page) to guide implementation toward specific components and treatments',
      'Iterate and test locally',
      <>
        Run <Cmd>/cai-language-review</Cmd>
      </>,
      <>
        Run <Cmd>/cai-design-review</Cmd>
      </>,
      <>
        Add any new, reusable components using <Cmd>/cai-add-to-design-system</Cmd>
      </>,
      <>
        Use <Cmd>/ship</Cmd> to open a PR
      </>,
      'Request a review from an engineer',
    ],
    document: FEEDBACK_CHANNEL,
    notes: (
      <>
        Please seek feedback on your work once in staging. <Tags>#have-some-taste #care-deeply</Tags>
      </>
    ),
  },
  {
    making: 'Prototyped idea',
    start: START_WITH_CLAUDE_CODE,
    how: [
      <>
        Use <Cmd>/work</Cmd> to set up branch, worktree, local environment, etc.
      </>,
      <>
        Run <Cmd>/cai-prototype</Cmd> to confine your work to a place where everyone can see and play with it
      </>,
      'Open Design System (this page) to guide implementation toward specific components and treatments',
      'Iterate and test locally',
      <>
        Run <Cmd>/cai-language-review</Cmd>
      </>,
      <>
        Run <Cmd>/cai-design-review</Cmd>
      </>,
      <>
        Add any new, reusable components using <Cmd>/cai-add-to-design-system</Cmd>
      </>,
      <>
        Use <Cmd>/ship</Cmd> to open a PR
      </>,
      'Request a review from an engineer',
    ],
    document: FEEDBACK_CHANNEL,
    notes: (
      <div className="flex flex-col gap-2">
        <p>
          <span className="text-ink font-medium">Important:</span> Prototypes are best suited to start in
          Claude Code, so they use our real components and data from the start.
        </p>
        <Bullets
          items={[
            <>
              Strongly recommend using the <Cmd>/cai-prototype</Cmd> skill, so that everyone can understand
              the intention of your work and play around with it
            </>,
          ]}
        />
        <Tags>#always-be-experimenting</Tags>
      </div>
    ),
  },
]

export function HowToMakeStuffSection() {
  return (
    // Unboxed, unlike the reference sections — this belongs to the page intro.
    <section id="how-to-make-stuff" className="scroll-mt-10">
      <h2 className="text-ink text-2xl font-bold tracking-[-0.01em]">How to make stuff</h2>
      <Table className="mt-6">
        <TableHeader>
          <TableRow>
            <TableHead className="w-44">What you&apos;re making</TableHead>
            <TableHead className="w-80">How to make it</TableHead>
            <TableHead className="w-40">Where to document</TableHead>
            <TableHead>Notes</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {WORKFLOWS.map((workflow) => (
            <TableRow key={workflow.making}>
              <TableCell className="text-ink align-top font-medium whitespace-normal">
                {workflow.making}
              </TableCell>
              <TableCell className="text-ink-600 align-top whitespace-normal">
                <div className="flex flex-col gap-1">
                  <div>{workflow.start}</div>
                  {workflow.stepsLead ? <div>{workflow.stepsLead}</div> : null}
                  <Steps items={workflow.how} />
                </div>
              </TableCell>
              <TableCell className="text-ink align-top whitespace-normal">{workflow.document}</TableCell>
              <TableCell className="text-ink-500 align-top leading-relaxed whitespace-normal">
                {Array.isArray(workflow.notes) ? <Bullets items={workflow.notes} /> : workflow.notes}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </section>
  )
}
